import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useRouteMatch} from 'react-router-dom';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import Form_13View from './Form_13-view';
import {CreateShumaContext, defaultContext} from '../CreateShumaContext';
import {
  useDecisionTreeOptionsContext,
  useDecisionTreeShumaTypesOptionsContext
} from '../../../../common/components/DecisionTreeOptions';
import {pathnames as P} from '../../../../common/constants/pathnames';
import {normalizeFormValues} from '../../../../common/utils/form/normalizeFormValues';
import SpinnerContainer from '../../../../common/components/SpinnerContainer';
import {submitStep} from '../../actions/steps/submitStep';
import {getStepData} from '../../actions/steps/getStepData';
import {getShumaStepStatus} from '../../actions/steps/getShumaStepStatus';
import {showGlobalErrorMessage} from '../../../../common/actions/globalErrorMessage/showGlobalErrorMessage';
import {updateCustomers} from '../../actions/steps/updateCustomers';

const Form_13 = () => {
  const dispatch = useDispatch();
  const stepData = useSelector(state => state.stepData.stepData, shallowEqual);
  const [reinit, setReinit] = useState(true);
  const [loading, setLoading] = useState(false);
  const {
    bankBranchName,
    bankBranchNumber,
    bankBuildType,
    bankClerkFullName,
    bankEmail,
    bankID,
    bankRequestID,
    loanText,
    loanTypeID,
    otherPropertyTypeID,
    propertyTypeID,
    shumaReasonID,
    shumaTypeID,
    loanSubTypeID,
    shumaMazminIdentityID,
    inspectionAtRisk,
    inspectionAtRiskDesc,
    underConstruction,
  } = stepData;

  const {
    reasons,
    banks,
    propertyTypes,
    loanTypes,
    loanSubTypes,
    shumaMazminIdentities
  } = useDecisionTreeOptionsContext();
  const {shumaTypes} = useDecisionTreeShumaTypesOptionsContext();

  const [previousShumaType] = (shumaTypeID) ? shumaTypes.filter(opt => opt.value === shumaTypeID) : [];
  const [previousBank] = bankID ? banks.filter(opt => opt.value === bankID) : [];
  const [previousPropertyType] = propertyTypeID ? propertyTypes.filter(opt => opt.value === propertyTypeID) : [];
  const [previousLoanType] = loanTypeID ? loanTypes.filter(opt => opt.value === loanTypeID) : [];
  const [previousOtherPropertyType] = otherPropertyTypeID ? propertyTypes.filter(opt => opt.value === otherPropertyTypeID) : [];
  const [previousLoanSubTypeID] = loanSubTypeID ? loanSubTypes.filter(opt => opt.value === loanSubTypeID) : [];
  const [previousShumaMazminIdentityID] = shumaMazminIdentityID ? shumaMazminIdentities.filter(opt => opt.value === shumaMazminIdentityID) : [];

  const initialValuesStep13 = {
    shumaReasonID: Boolean(shumaReasonID) ? `${shumaReasonID}` : null,
    shumaTypeID: Boolean(shumaTypeID) ? previousShumaType : null,
    bankID: Boolean(bankID) ? previousBank : null,
    propertyTypeID: Boolean(propertyTypeID) ? previousPropertyType : null,
    loanTypeID: Boolean(loanTypeID) ? previousLoanType : null,
    loanText: loanText || '',
    otherPropertyTypeID: Boolean(otherPropertyTypeID) ? previousOtherPropertyType : null,
    bankBranchNumber: bankBranchNumber || null,
    bankBranchName: bankBranchName || null,
    bankClerkFullName: bankClerkFullName || null,
    bankEmail: bankEmail || null,
    bankRequestID: bankRequestID || null,
    bankBuildType: bankBuildType || null,
    loanSubTypeID: Boolean(loanSubTypeID) ? previousLoanSubTypeID : null,
    shumaMazminIdentityID: Boolean(shumaMazminIdentityID) ? previousShumaMazminIdentityID : null,
    inspectionAtRisk: Boolean(inspectionAtRisk) || null,
    inspectionAtRiskDesc: inspectionAtRiskDesc || '',
    underConstruction: Boolean(underConstruction) || null,
  };

  const {
    params: {
      shumaId,
      formId: stepId
    }
  } = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const submitWithoutFormReinitialization = values => {
    const {
      shumaReasonID,
      propertyTypeID,
      bankID,
      loanTypeID,
      loanText,
      otherPropertyTypeID,
      bankBranchNumber,
      bankBranchName,
      bankClerkFullName,
      bankEmail,
      bankRequestID,
      bankBuildType,
      loanSubTypeID,
      shumaMazminIdentityID,
      inspectionAtRisk,
      inspectionAtRiskDesc,
      underConstruction,
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      shumaReasonID: parseInt(shumaReasonID, 10),
      propertyTypeID,
      bankID,
      loanTypeID,
      loanText,
      otherPropertyTypeID,
      bankBranchNumber,
      bankBranchName,
      bankClerkFullName,
      bankEmail,
      bankRequestID,
      bankBuildType,
      loanSubTypeID,
      shumaMazminIdentityID,
      inspectionAtRisk,
      inspectionAtRiskDesc,
      underConstruction,
    };
    setReinit(false);
    return dispatch(submitStep({step: 13, data}))
      .then(() => setReinit(true));
  };

  const handleDataSubmit = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => dispatch(getShumaStepStatus({shumaId, resetValues: false})))
        .catch((err) => dispatch(showGlobalErrorMessage(err.message)));
    }
  };

  const context = {
    ...defaultContext,
    onSubmit: (values, shumaId, dirty) => {
      handleDataSubmit(values, shumaId, dirty);
    },
    onSubmitSuccess: () => {
      dispatch(getStepData({shumaId, stepId}));
    },
    onCustomerCreated: async () => {
      await dispatch(getStepData({shumaId, stepId}));
    },
    onCustomerEdited: async (customer) => {
      const customers = stepData.customers.map(item => {
        return item.customerID === customer.customerID
          ? {
            ...item,
            customerFirstName: customer.firstName,
            customerLastName: customer.lastName,
            customerIdNumber: customer.idNumber,
          }
          : item;
      });
      await dispatch(updateCustomers(customers));
      await dispatch(getStepData({shumaId, stepId}));
    },
    clearInspectionData: () => {},
    disabledFirstShumaReasonID: false,
    initialValuesStep13: initialValuesStep13,
    decisionTreeOptions: {
      reasons,
      banks,
      propertyTypes,
      loanTypes,
      loanSubTypes,
      shumaMazminIdentities
    },
    options: {
      ...defaultContext.options,
      shumaId: shumaId,
      reinit: reinit,
      disabled: ['shumaReasonID', 'shumaTypeID', 'propertyTypeID'],
    },
    stepData,
    setLoading
  };

  return (
    <CreateShumaContext.Provider value={context}>
      {loading ? <SpinnerContainer/> : <Form_13View/>}
    </CreateShumaContext.Provider>
  );
};

Form_13.propTypes = {
  submitStep: PropTypes.func,
  getStepData: PropTypes.func,
  stepData: PropTypes.object,
  getShumaStepStatus: PropTypes.func,
  showGlobalErrorMessage: PropTypes.func,
};

export default Form_13;
